import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { SearchForm } from '@/components/search/SearchForm';
import { SearchTypeSelector } from '@/components/search/SearchTypeSelector';
import type { SearchType, SingleSearchRequest } from '@/types';
import type { SingleSearchFormData } from '@/utils/searchValidation';
import { logger } from '@/utils/logger';

export const SearchInputPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<SearchType>('scoring_enrich');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (data: SingleSearchFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      logger.info('Preparing search request for payment', {
        firstName: data.firstName,
        lastName: data.lastName,
        searchType: data.searchType,
      });

      const searchRequest: SingleSearchRequest = {
        firstName: data.firstName,
        lastName: data.lastName,
        amountToCollect: data.amountToCollect,
        searchType: data.searchType,
        address: data.address || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        zip: data.zip || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
      };

      // Store search request in sessionStorage (will execute after payment)
      const searchId = `search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(`search_request_${searchId}`, JSON.stringify(searchRequest));

      logger.info('Search request stored, navigating to payment', { searchId });

      // Navigate to payment page
      navigate(`/search/payment?searchId=${searchId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to prepare search. Please try again.';
      logger.error('Search preparation failed:', err);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Individual CollectABILITY Search
          </h1>

          <p className="text-sm text-slate-600 max-w-2xl mx-auto">
            Get instant AI-powered insights for any individual. Pay only for what you need.
          </p>
        </motion.div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Left Column - Search Type Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Choose Your Search Type</h2>
            <SearchTypeSelector value={searchType} onChange={setSearchType} />
          </motion.div>

          {/* Right Column - Search Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Enter Search Details</h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <SearchForm
              searchType={searchType}
              onSubmit={(data) => handleSubmit({ ...data, searchType })}
              isLoading={isSubmitting}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

